import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";

import { getTurnstileConfig, postContactEntreprise } from "../../api/Message";

import PortalModal from "../PortalModal";

// --------------------------------------------------
// DONNÉES INITIALES
// --------------------------------------------------

const initialData = {
    lastname: "",
    firstname: "",
    company: "",
    email: "",
    nb_pizza: "",
    date: "",
    hour: "",
    content: "",
};

export default function EventForm() {
    const [step, setStep] = useState(1);
    const maxStep = 3;

    const [data, setData] = useState(initialData);

    const [siteKey, setSiteKey] = useState("");
    const [captchaToken, setCaptchaToken] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [isFinish, setIsFinish] = useState(false);
    const [error, setError] = useState("");

    const turnstileRef = useRef(null);
    const submittingRef = useRef(false);

    // --------------------------------------------------
    // RÉCUPÉRATION DE LA CLÉ PUBLIQUE TURNSTILE
    // --------------------------------------------------

    useEffect(() => {
        let active = true;

        const loadTurnstileConfig = async () => {
            try {
                const config = await getTurnstileConfig();

                if (active) {
                    if (!config?.siteKey) {
                        throw new Error("Clé Turnstile absente");
                    }

                    setSiteKey(config.siteKey);
                }
            } catch (err) {
                console.error("Erreur configuration Turnstile :", err);

                if (active) {
                    setError("Le service de vérification est indisponible. Veuillez réessayer plus tard.");
                }
            }
        };

        loadTurnstileConfig();

        return () => {
            active = false;
        };
    }, []);

    // --------------------------------------------------
    // CHANGEMENT DES CHAMPS
    // --------------------------------------------------

    const handleChange = (e) => {
        const { name, value } = e.target;

        setData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // --------------------------------------------------
    // RÉINITIALISATION DU CAPTCHA
    // --------------------------------------------------

    const resetCaptcha = () => {
        setCaptchaToken("");
        turnstileRef.current?.reset();
    };

    // --------------------------------------------------
    // SOUMISSION DU FORMULAIRE
    // --------------------------------------------------

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (step < maxStep) {
            setError("");
            setStep((prev) => prev + 1);
            return;
        }

        if (submittingRef.current) return;

        if (!captchaToken) {
            setError("Veuillez valider le CAPTCHA avant l'envoi.");
            return;
        }

        submittingRef.current = true;
        setIsLoading(true);
        setError("");

        try {
            await postContactEntreprise(data.lastname, data.firstname, data.company, data.email, data.nb_pizza, data.date, data.hour, data.content, captchaToken);

            setIsFinish(true);
            setData(initialData);
            setStep(1);
        } catch (err) {
            console.error("Erreur lors de l'envoi :", err);

            setError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
        } finally {
            resetCaptcha();
            submittingRef.current = false;
            setIsLoading(false);
        }
    };

    // --------------------------------------------------
    // AFFICHAGE
    // --------------------------------------------------

    return (
        <>
            <div className="event-form-container flex flex-col">
                {!isLoading ? (
                    <>
                        {/* ÉTAPE 1 : INFORMATIONS */}

                        {step === 1 && (
                            <>
                                <h2 className="uppercase text-center">Informations</h2>

                                <form onSubmit={handleSubmit} className="flex flex-col" autoComplete="on">
                                    <div className="flex flex-col gap-[8px]">
                                        <label htmlFor="nb_pizza">Nombre de pizzas</label>

                                        <input required min="1" type="number" name="nb_pizza" id="nb_pizza" onChange={handleChange} value={data.nb_pizza} />
                                    </div>

                                    <div className="flex gap-[24px] name-container">
                                        <div className="flex flex-col gap-[8px] w-full">
                                            <label htmlFor="date">Date de l'événement</label>

                                            <input required type="date" name="date" id="date" onChange={handleChange} value={data.date} />
                                        </div>

                                        <div className="flex flex-col gap-[8px] w-full">
                                            <label htmlFor="hour">Heure de l'événement</label>

                                            <input required type="time" name="hour" id="hour" onChange={handleChange} value={data.hour} />
                                        </div>
                                    </div>

                                    <button type="submit" className="w-full bg-primary text-center">
                                        Étape suivante
                                    </button>
                                </form>
                            </>
                        )}

                        {/* ÉTAPE 2 : MESSAGE */}

                        {step === 2 && (
                            <>
                                <h2 className="uppercase text-center">Votre message</h2>

                                <form onSubmit={handleSubmit} className="flex flex-col">
                                    <div className="flex flex-col gap-[8px]">
                                        <label htmlFor="content">Message</label>

                                        <textarea required name="content" id="content" rows="5" onChange={handleChange} value={data.content} />
                                    </div>

                                    <button type="submit" className="w-full bg-primary text-center">
                                        Étape suivante
                                    </button>
                                </form>
                            </>
                        )}

                        {/* ÉTAPE 3 : IDENTITÉ + CAPTCHA */}

                        {step === 3 && (
                            <>
                                <h2 className="uppercase text-center">Votre identité</h2>

                                <form onSubmit={handleSubmit} className="flex flex-col">
                                    <div className="flex gap-[24px] name-container">
                                        <div className="flex flex-col gap-[8px] w-full">
                                            <label htmlFor="lastname">Nom</label>

                                            <input required type="text" name="lastname" id="lastname" autoComplete="family-name" onChange={handleChange} value={data.lastname} />
                                        </div>

                                        <div className="flex flex-col gap-[8px] w-full">
                                            <label htmlFor="firstname">Prénom</label>

                                            <input required type="text" name="firstname" id="firstname" autoComplete="given-name" onChange={handleChange} value={data.firstname} />
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-[8px]">
                                        <label htmlFor="company">Société</label>

                                        <input required type="text" name="company" id="company" autoComplete="organization" onChange={handleChange} value={data.company} />
                                    </div>

                                    <div className="flex flex-col gap-[8px]">
                                        <label htmlFor="email">Email</label>

                                        <input required type="email" name="email" id="email" autoComplete="email" onChange={handleChange} value={data.email} />
                                    </div>

                                    {/* CLOUDFLARE TURNSTILE */}

                                    <div className="my-6">
                                        {siteKey ? (
                                            <Turnstile
                                                ref={turnstileRef}
                                                siteKey={siteKey}
                                                options={{
                                                    theme: "auto",
                                                    size: "normal",
                                                }}
                                                onSuccess={(token) => {
                                                    setCaptchaToken(token);
                                                    setError("");
                                                }}
                                                onExpire={() => {
                                                    setCaptchaToken("");
                                                }}
                                                onError={() => {
                                                    setCaptchaToken("");
                                                    setError("La vérification CAPTCHA a échoué. Veuillez réessayer.");
                                                }}
                                            />
                                        ) : (
                                            <p className="text-sm text-white">Chargement de la vérification de sécurité...</p>
                                        )}
                                    </div>

                                    {/* MESSAGE D'ERREUR */}

                                    {error && (
                                        <p role="alert" className="text-red-400 text-sm mb-4">
                                            {error}
                                        </p>
                                    )}

                                    {/* BOUTON ENVOYER */}

                                    <button type="submit" disabled={isLoading || !captchaToken} className="w-full bg-primary text-center disabled:opacity-50 disabled:cursor-not-allowed">
                                        {isLoading ? "Envoi en cours..." : "Envoyer"}
                                    </button>
                                </form>
                            </>
                        )}
                    </>
                ) : (
                    <div className="justify-center items-center flex flex-col gap-4 p-4">
                        <div className="loader"></div>
                    </div>
                )}
            </div>

            {/* MODALE DE CONFIRMATION */}

            <PortalModal isOpen={isFinish} onClose={() => setIsFinish(false)}>
                <div className="text-center flex flex-col items-center justify-center gap-4">
                    <p className="font-bold text-4xl">Message envoyé avec succès !</p>

                    <p className="text-lg">Nous vous contacterons dès que possible.</p>

                    <Link to="/" className="w-fit py-2 px-3 font-bold bg-primary text-center rounded-lg">
                        Retour à l'accueil
                    </Link>
                </div>
            </PortalModal>
        </>
    );
}
